import {
    AccrualReviewStatus,
    DocumentTaxonomyExtraction,
    ExtractionStatus,
    FinancialDocument,
    FinancialDocumentBase,
    ImporterErrors,
    LoanStatementExtraction,
    SpreadsheetImporterStatus,
    TransactionsImportStatus,
} from "../../types/financialDocument";
import { getCashAppExcludedLinkedAccountPaymentCount } from "../../helpers/financialDocument";
import { getAccrualReviewStatus } from "../../helpers/accrualReviewStatus";
import { unpackDateOrNull } from "../dto-helpers";

type FinancialDocumentDtoInput = FinancialDocumentBase;

/**
 * Main DTO for FinancialDocument - scalars only, no relations.
 * Used when relations are not loaded (e.g., get-documents-for-financial-account,
 * nested in TransactionMatchDto, IntegrationAccountDto).
 */
export class FinancialDocumentDto {
    static deserialize(dto: FinancialDocumentDto): FinancialDocument {
        return {
            ...dto,
            createdAt: new Date(dto.createdAt),
            date: unpackDateOrNull(dto.date),
            externalExtendFileId: dto.externalExtendFileId ?? null,
            externalExtendLightParseFileId:
                dto.externalExtendLightParseFileId ?? null,
            cashAppExcludedLinkedAccountPaymentCount:
                dto.cashAppExcludedLinkedAccountPaymentCount ?? null,
        };
    }

    id: number;
    source: string;
    createdAt: string;
    path: string;
    fileName: string;
    originalFileName: string | null;
    hasAttemptedMatching: boolean;
    ocrSuccessful: boolean;
    amount: number | null;
    date: string | null;
    spreadsheetImporterStatus: SpreadsheetImporterStatus;
    spreadsheetParsingRuleId: number | null;
    isSpreadsheet: boolean | null;
    numTransactions: number | null;
    csvFormatStr: string | null;
    entityId: number | null;
    financialAccountId: number | null;
    integrationAccountId: string | null;
    accountInfoExtracted: boolean;
    taxonomyExtraction: DocumentTaxonomyExtraction | null;
    taxonomyExtractionStatus: ExtractionStatus;
    loanExtraction: LoanStatementExtraction | null;
    loanExtracted: boolean;
    importerErrors: ImporterErrors[] | null;
    dismissedImporterErrors: ImporterErrors[] | null;
    openingBalance: number | null;
    closingBalance: number | null;
    openingDate: string | null;
    closingDate: string | null;
    transactionsSpreadsheetId: string | null;
    transactionsImportStatus: TransactionsImportStatus | null;
    externalExtendFileId: string | null;
    externalExtendLightParseFileId: string | null;
    importerQueue: string | null;
    accrualReviewStatus: AccrualReviewStatus | null;
    documentCategoryId: string | null;
    counterpartyId: string | null;
    cashAppExcludedLinkedAccountPaymentCount: number | null;
    isReconciled?: boolean;
    integrationAccount?: { financialAccountId: number | null } | null;

    constructor(financialDocument: FinancialDocumentDtoInput) {
        this.id = financialDocument.id;
        this.source = financialDocument.source;
        this.createdAt = financialDocument.createdAt.toISOString();
        this.path = financialDocument.path;
        this.fileName = financialDocument.fileName;
        this.originalFileName = financialDocument.originalFileName ?? null;
        this.hasAttemptedMatching = financialDocument.hasAttemptedMatching;
        this.ocrSuccessful = financialDocument.ocrSuccessful;
        this.amount = financialDocument.amount ?? null;
        this.date = financialDocument.date?.toISOString() ?? null;
        this.spreadsheetImporterStatus =
            financialDocument.spreadsheetImporterStatus ?? null;
        this.spreadsheetParsingRuleId =
            financialDocument.spreadsheetParsingRuleId ?? null;
        this.isSpreadsheet = financialDocument.isSpreadsheet ?? null;
        this.numTransactions = financialDocument.numTransactions ?? null;
        this.csvFormatStr = financialDocument.csvFormatStr ?? null;
        this.entityId = financialDocument.entityId;
        this.financialAccountId = financialDocument.financialAccountId;
        this.integrationAccountId = financialDocument.integrationAccountId;
        this.accountInfoExtracted = financialDocument.accountInfoExtracted;
        this.taxonomyExtraction = financialDocument.taxonomyExtraction;
        this.taxonomyExtractionStatus =
            financialDocument.taxonomyExtractionStatus;
        this.loanExtraction = financialDocument.loanExtraction;
        this.loanExtracted = financialDocument.loanExtracted;
        this.importerErrors = financialDocument.importerErrors;
        this.dismissedImporterErrors =
            financialDocument.dismissedImporterErrors;
        this.openingBalance = financialDocument.openingBalance;
        this.closingBalance = financialDocument.closingBalance;
        this.openingDate = financialDocument.openingDate;
        this.closingDate = financialDocument.closingDate;
        this.transactionsSpreadsheetId =
            financialDocument.transactionsSpreadsheetId;
        this.transactionsImportStatus =
            financialDocument.transactionsImportStatus;
        this.externalExtendFileId = financialDocument.externalExtendFileId;
        this.externalExtendLightParseFileId =
            financialDocument.externalExtendLightParseFileId;
        this.importerQueue = financialDocument.importerQueue;
        this.accrualReviewStatus = getAccrualReviewStatus(financialDocument);
        this.documentCategoryId = financialDocument.documentCategoryId;
        this.counterpartyId = financialDocument.counterpartyId;
        this.cashAppExcludedLinkedAccountPaymentCount =
            getCashAppExcludedLinkedAccountPaymentCount(
                financialDocument.importerOutput,
            ) ?? null;
        if (financialDocument.bankReconciliationDocuments !== undefined) {
            this.isReconciled =
                financialDocument.bankReconciliationDocuments.length > 0;
        }
        if (financialDocument.integrationAccount) {
            this.integrationAccount = {
                financialAccountId:
                    financialDocument.integrationAccount.financialAccountId,
            };
        }
    }
}
